import React, { useState, useEffect } from 'react';
import { locationService } from '../../services/api';
import { getClinicsByCity } from '../../services/api';
import PropTypes from 'prop-types';

const LocationSelector = ({ onLocationSelect, onFetchClinics, showFetchButton = true }) => {
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [selectedState, setSelectedState] = useState('');
    const [selectedCity, setSelectedCity] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [fetchingClinics, setFetchingClinics] = useState(false);

    // Fetch states on component mount
    useEffect(() => {
        const fetchStates = async () => {
            try {
                setLoading(true);
                const response = await locationService.getStates();
                if (response.success && Array.isArray(response.data)) {
                    setStates(response.data);
                } else {
                    setStates([]);
                    setError('Failed to load states');
                }
            } catch (err) {
                console.error('Error fetching states:', err);
                setStates([]);
                setError('Failed to load states');
            } finally {
                setLoading(false);
            }
        };

        fetchStates();
    }, []);

    // Fetch cities when state is selected
    useEffect(() => {
        const fetchCities = async () => {
            if (!selectedState) {
                setCities([]);
                setSelectedCity('');
                return;
            }

            try {
                setLoading(true);
                const response = await locationService.getCities(selectedState);
                if (response.success && Array.isArray(response.data)) {
                    setCities(response.data);
                } else {
                    setCities([]);
                    setError('Failed to load cities');
                }
            } catch (err) {
                console.error('Error fetching cities:', err);
                setCities([]);
                setError('Failed to load cities');
            } finally {
                setLoading(false);
            }
        };

        fetchCities();
    }, [selectedState]);

    const handleStateChange = (e) => {
        const stateId = e.target.value;
        setSelectedState(stateId);
        setSelectedCity(''); // Reset city selection when state changes
        onLocationSelect({ stateId, cityId: '' });
    };

    const handleCityChange = (e) => {
        const cityId = e.target.value;
        setSelectedCity(cityId);
        onLocationSelect({ stateId: selectedState, cityId });
    };

    const handleFetchClinics = async () => {
        if (!selectedCity) {
            setError('Please select a city first');
            return;
        }

        try {
            setFetchingClinics(true);
            setError('');
            const response = await getClinicsByCity({cityId: selectedCity});
            
            if (response.success && response.data) {
                onFetchClinics(response.data.data);
            } else {
                setError(response.message || 'Failed to fetch clinics');
            }
        } catch (err) {
            console.error('Error fetching clinics:', err);
            setError('Failed to fetch clinics');
        } finally {
            setFetchingClinics(false);
        }
    };

    if (loading) {
        return <div className="text-center py-4">Loading locations...</div>;
    }

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        State
                    </label>
                    <select
                        value={selectedState}
                        onChange={handleStateChange}
                        className="input"
                    >
                        <option value="">Select State</option>
                        {Array.isArray(states) && states.map((state) => (
                            <option key={state.stateId} value={state.stateId}>
                                {state.stateName}
                            </option>
                        ))}
                    </select>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        City
                    </label>
                    <select
                        value={selectedCity}
                        onChange={handleCityChange}
                        className="input"
                        disabled={!selectedState}
                    >
                        <option value="">Select City</option>
                        {Array.isArray(cities) && cities.map((city) => (
                            <option key={city.cityId} value={city.cityId}>
                                {city.cityName}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {error && (
                <div className="text-red-500 text-sm">{error}</div>
            )}

            {showFetchButton && (
                <div className="flex justify-end">
                    <button
                        onClick={handleFetchClinics}
                        disabled={!selectedCity || fetchingClinics}
                        className={`px-4 py-2 rounded-md text-white font-medium ${
                            selectedCity && !fetchingClinics
                                ? 'bg-indigo-600 hover:bg-indigo-700' 
                                : 'bg-gray-400 cursor-not-allowed'
                        }`}
                    >
                        {fetchingClinics ? 'Fetching...' : 'Fetch Clinics'}
                    </button>
                </div>
            )}
        </div>
    );
};

LocationSelector.propTypes = {
    onLocationSelect: PropTypes.func.isRequired,
    onFetchClinics: PropTypes.func,
    showFetchButton: PropTypes.bool
};

export default LocationSelector; 